import { QueryOptions, WithRequired } from '@tanstack/react-query';

import {
  getAllFlowMessagesNeedDeliverabilityBlockUpgrade,
  getAllMisconfiguredExperiments,
  getAllNeedUniversalBlockExperiments,
  getAllPendingFlowMessages,
  getBASessionArtifacts,
  getBASessionConsoleLog,
  getBrowserAutomationHealth,
  getBrowserAutomationSession,
  getBrowserAutomationSessions,
  getCampaignCoverageAlerts,
  getCSBrowserSessions,
  getCampaignMonthlyDeliverabilityMetrics,
  getCampaignPrimaryConversionMetricOptions,
  getCampaignSetupAutomationPauseStatus,
  getCombinedMonthlyOptimizerMetrics,
  getCustomerFeedbacksForStore,
  getCustomerFeedbacksForUsecase,
  getDataQualityReport,
  getChurnedStores,
  getEnabledStores,
  getEnabledStoresFinancials,
  getFlowActionsForStoreKey,
  getFlowMessage,
  getFlowMessageExperimentAllocationConversionStats,
  getFlowMessageExperimentTargetMetrics,
  getFlowMessagesForStoreKey,
  getFlowMonthlyOptimizerMetrics,
  getFlowsForStoreKey,
  getGrowthReport,
  getKlaviyoAccount,
  getMissedCampaignDeliverabilityBlocks,
  getOptimizerCampaignDeliverabilityExperimentMetrics,
  getOptInMonitoringAuditLog,
  getOptInMonitoringBaseline,
  getOptInMonitoringCompliance,
  getOptInMonitoringReadback,
  getOptInMonitoringStoreSummary,
  getOptInMonitoringStores,
  getOrderRateLiftAlerts,
  getOrganizationMembers,
  getPretesterInboxes,
  getSPOptimizerCampaignDeliverabilityBlockSetups,
  getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
  getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
  getSPOptimizerFlowMessageExperimentVariantsForExperiment,
  getSPOptimizerFlowMessageExperimentsForFlowMessage,
  getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentsForStore,
  getSPOptimizerStore,
  getSPOptimizerStoreAggregatedSummaryFlowMetrics,
  getSPOptimizerStoreBatchAggregatedSummaryFlowMetrics,
  getSPOptimizerStoreDeliverabilityBlockSetup,
  getSPOptimizerStoreDeliverabilityBlockSetups,
  getSPOptimizerStoreExcludePhrases,
  getSPOptimizerStoreIncludePhrases,
  getSPOptimizerStores,
  getSavedVideoScripts,
  getSetupDecisions,
  getShopifyShop,
  getSlackAnnouncement,
  getSlackAnnouncements,
  getSlackChannelMappings,
  getStoreAggregatedMetrics,
  getStoreAggregatedMetricsPerUseCase,
  getStoreConsentAuditLog,
  getStoreConsentAuditLogLive,
  getStoreConsentAuditLogProfile,
  getStoreAmpSummary,
  getStoreConsentOptinSummary,
  getStoreDailyMetrics,
  getStores,
  getTrialExpirations,
  getTrialExpirationsIncrementalRevenue,
  getUsecase,
  getUsecasesForStore,
  postShopifyCartProductImages,
} from './methods';
import type { ConsentAuditLogFilters } from './methods';

const makeQuery = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions: (
    ...args: [...Parameters<F>]
  ) => Omit<WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryKey'>, 'queryFn'>,
): ((...args: [...Parameters<F>]) => WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryFn' | 'queryKey'>) => {
  return (...args) => ({
    queryFn: () => apiMethod(...args),
    ...otherOptions(...args),
  });
};

export const queries = {
  stores: makeQuery(getStores, () => ({
    queryKey: ['stores'],
  })),
  growthReport: makeQuery(getGrowthReport, (product: 'all' | 'video' | 'optimizer') => ({
    queryKey: ['growth-report', product],
  })),
  enabledStores: makeQuery(getEnabledStores, () => ({
    queryKey: ['enabled-stores'],
  })),
  enabledStoresFinancials: makeQuery(getEnabledStoresFinancials, () => ({
    queryKey: ['enabled-stores-financials'],
  })),
  churnedStores: makeQuery(getChurnedStores, () => ({
    queryKey: ['churned-stores'],
  })),
  trialExpirations: makeQuery(getTrialExpirations, () => ({
    queryKey: ['trial-expirations'],
  })),
  trialExpirationsIncrementalRevenue: makeQuery(getTrialExpirationsIncrementalRevenue, () => ({
    queryKey: ['trial-expirations-incremental-revenue'],
  })),
  usecase: makeQuery(getUsecase, (organization: string, usecaseId: number) => ({
    queryKey: ['organization', organization, 'usecase', usecaseId],
  })),
  flowMessage: makeQuery(getFlowMessage, (flowMessageID: number) => ({
    queryKey: ['flow-message', flowMessageID],
  })),
  usecasesForStore: makeQuery(
    getUsecasesForStore,
    (organization: string | undefined, storeKey: string | undefined) => ({
      queryKey: ['organization', organization, 'usecases', storeKey],
    }),
  ),
  spOptimizerStores: makeQuery(getSPOptimizerStores, (organization: string) => ({
    queryKey: ['organization', organization, 'spOptimizerStores'],
  })),
  spOptimizerStore: makeQuery(getSPOptimizerStore, (organization: string, spOptimizerStoreID: number) => ({
    queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreID],
  })),
  spOptimizerStoreExcludePhrases: makeQuery(
    getSPOptimizerStoreExcludePhrases,
    (organization: string, spOptimizerStoreID: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStoreExcludePhrases', spOptimizerStoreID],
    }),
  ),
  spOptimizerStoreIncludePhrases: makeQuery(
    getSPOptimizerStoreIncludePhrases,
    (organization: string, spOptimizerStoreID: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStoreIncludePhrases', spOptimizerStoreID],
    }),
  ),
  spOptimizerStoreDeliverabilityBlockSetups: makeQuery(getSPOptimizerStoreDeliverabilityBlockSetups, () => ({
    queryKey: ['spOptimizerStoreDeliverabilityBlockSetups'],
  })),
  spOptimizerStoreDeliverabilityBlockSetup: makeQuery(
    getSPOptimizerStoreDeliverabilityBlockSetup,
    (spOptimizerStoreID: number) => ({
      queryKey: ['spOptimizerStoreDeliverabilityBlockSetup', spOptimizerStoreID],
    }),
  ),
  spOptimizerCampaignDeliverabilityBlockSetups: makeQuery(
    getSPOptimizerCampaignDeliverabilityBlockSetups,
    (setupStatuses: string[], page = 1, pageSize = 50, storeID?: number) => ({
      queryKey: ['spOptimizerCampaignDeliverabilityBlockSetups', setupStatuses, page, pageSize, storeID],
    }),
  ),
  missedCampaignDeliverabilityBlocks: makeQuery(
    getMissedCampaignDeliverabilityBlocks,
    (page = 1, pageSize = 50, storeID?: number, search?: string) => ({
      queryKey: ['missedCampaignDeliverabilityBlocks', page, pageSize, storeID, search],
    }),
  ),
  campaignSetupAutomationPauseStatus: makeQuery(getCampaignSetupAutomationPauseStatus, () => ({
    queryKey: ['campaignSetupAutomationPauseStatus'],
  })),
  storeAggregatedMetrics: makeQuery(
    getStoreAggregatedMetrics,
    (organization: string, storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'usecase', useCaseId, 'aggregate-metrics', days],
    }),
  ),
  storeAggregatedMetricsPerUseCase: makeQuery(
    getStoreAggregatedMetricsPerUseCase,
    (organization: string, storeKey: string, days: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'aggregate-metrics-per-usecase', days],
    }),
  ),
  storeDailyMetrics: makeQuery(
    getStoreDailyMetrics,
    (organization: string, storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'usecase', useCaseId, 'daily-metrics', days],
    }),
  ),
  storeAmpSummary: makeQuery(getStoreAmpSummary, (organization: string, storeKey: string, days: number) => ({
    queryKey: ['organization', organization, 'store', storeKey, 'amp-summary', days],
  })),
  storeConsentOptinSummary: makeQuery(
    getStoreConsentOptinSummary,
    (organization: string, storeKey: string, days: number) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'consent-optin-summary', days],
    }),
  ),
  storeConsentAuditLog: makeQuery(
    getStoreConsentAuditLog,
    (organization: string, storeKey: string, filters: Omit<ConsentAuditLogFilters, 'organization'>) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'consent-audit-log', filters],
    }),
  ),
  storeConsentAuditLogProfile: makeQuery(
    getStoreConsentAuditLogProfile,
    (organization: string, storeKey: string, profileId: string) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'consent-audit-log', 'profile', profileId],
    }),
  ),
  storeConsentAuditLogLive: makeQuery(
    getStoreConsentAuditLogLive,
    (organization: string, storeKey: string, profileId: string) => ({
      queryKey: ['organization', organization, 'store', storeKey, 'consent-audit-log', 'live', profileId],
    }),
  ),
  storeFlowMonthlyOptimizerMetrics: makeQuery(
    getFlowMonthlyOptimizerMetrics,
    (organization: string | undefined, spOptimizerStoreID: number | undefined) => ({
      queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreID, 'flow', 'monthly-metrics'],
    }),
  ),
  storeCampaignMonthlyDeliverabilityMetrics: makeQuery(
    getCampaignMonthlyDeliverabilityMetrics,
    (organization: string, spOptimizerStoreId: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'spOptimizerStore',
        spOptimizerStoreId,
        'campaign-deliverability',
        'monthly-metrics',
      ],
    }),
  ),
  storeCombinedMonthlyOptimizerMetrics: makeQuery(
    getCombinedMonthlyOptimizerMetrics,
    (
      organization: string,
      spOptimizerStoreId: number | undefined,
      includeCampaigns?: boolean,
      includeFlows?: boolean,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'spOptimizerStore',
        spOptimizerStoreId,
        'combined',
        'monthly-metrics',
        includeCampaigns,
        includeFlows,
      ],
    }),
  ),
  flowMessageExperimentAllocationConversionStats: makeQuery(
    getFlowMessageExperimentAllocationConversionStats,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'flow-message-experiment-allocation-conversion-stats',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  flowMessageExperimentTargetMetrics: makeQuery(
    getFlowMessageExperimentTargetMetrics,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'flow-message-experiment-target-metrics',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  customerFeedbacksForUsecase: makeQuery(
    getCustomerFeedbacksForUsecase,
    (organization: string, usecaseId: number, page = 1, pageSize = 10) => ({
      queryKey: ['organization', organization, 'usecase', 'user-feedback', usecaseId, page, pageSize],
    }),
  ),
  customerFeedbacksForStore: makeQuery(
    getCustomerFeedbacksForStore,
    (organization: string, storeId: number | undefined, page = 1, pageSize = 25) => ({
      queryKey: ['organization', organization, 'store', 'user-feedback', storeId, page, pageSize],
    }),
  ),
  klaviyoAccount: makeQuery(getKlaviyoAccount, (organization: string, klaviyoAccountId: number) => ({
    queryKey: ['organization', organization, 'klaviyo-account', klaviyoAccountId],
  })),
  shopifyShop: makeQuery(getShopifyShop, (organization: string, shopifyShopId: number) => ({
    queryKey: ['organization', organization, 'shopify-shop', shopifyShopId],
  })),
  shopifyCartProductImages: makeQuery(
    postShopifyCartProductImages,
    (
      organization: string,
      storeKey: string,
      shopifyCheckoutId: string | undefined | null,
      shopifyCheckoutToken: string | undefined | null,
      shopifyProductIds: string[],
    ) => ({
      queryKey: [
        'organization',
        organization,
        'store',
        storeKey,
        'shopify-cart-product-images',
        shopifyCheckoutId,
        shopifyCheckoutToken,
        shopifyProductIds,
      ],
    }),
  ),
  savedVideoScripts: makeQuery(getSavedVideoScripts, (organization: string, useCaseId: number) => ({
    queryKey: ['organization', organization, 'usecase', useCaseId, 'get-saved-video-scripts'],
  })),
  spOptimizerFlowMessageExperimentsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
    (organization: string, spOptimizerStoreId: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiments',
        'for-sp-optimizer-store',
        spOptimizerStoreId,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentsForStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForStore,
    (organization: string | undefined, storeKey: string | undefined) => ({
      queryKey: ['organization', organization, 'sp-optimizer-flow-message-experiments', 'for-store', storeKey],
    }),
  ),
  spOptimizerFlowMessageExperimentsForFlowMessage: makeQuery(
    getSPOptimizerFlowMessageExperimentsForFlowMessage,
    (organization: string | undefined, flowMessageID: number | undefined) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiments',
        'for-flow-message',
        flowMessageID,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentMetricsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
    (organization: string, spOptimizerStoreId: number) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-metrics',
        'for-sp-optimizer-store',
        spOptimizerStoreId,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentVariantAggregatedMetrics: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
    (
      organization: string,
      spOptimizerFlowMessageExperimentVariantId: number,
      days: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-variant',
        spOptimizerFlowMessageExperimentVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  spOptimizerStoreAggregatedSummaryFlowMetrics: makeQuery(
    getSPOptimizerStoreAggregatedSummaryFlowMetrics,
    (
      organization: string,
      spOptimizerStoreId: number,
      days: number,
      flowId?: number,
      flowMessageId?: number,
      flowMessageVariantId?: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-store-aggregated-summary-flow-metrics',
        spOptimizerStoreId,
        'flow',
        flowId,
        'flow-message',
        flowMessageId,
        'flow-message-variant',
        flowMessageVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  spOptimizerStoreBatchAggregatedSummaryFlowMetrics: makeQuery(
    getSPOptimizerStoreBatchAggregatedSummaryFlowMetrics,
    (
      organization: string,
      spOptimizerStoreId: number,
      days: number,
      flowId?: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-store-batch-aggregated-summary-flow-metrics',
        spOptimizerStoreId,
        'flow',
        flowId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  flowsForStoreKey: makeQuery(getFlowsForStoreKey, (storeKey: string) => ({
    queryKey: ['flows-for-store-key', storeKey],
  })),
  flowMessageForStoreKey: makeQuery(getFlowMessagesForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-messages-for-store-key', storeKey],
  })),
  flowActionsForStoreKey: makeQuery(getFlowActionsForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-actions-for-store-key', storeKey],
  })),
  spOptimizerFlowMessageExperimentVariantsForExperiment: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantsForExperiment,
    (organization: string, spOptimizerFlowMessageExperimentId: number, onlyActive?: boolean) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-variants-for-experiment',
        spOptimizerFlowMessageExperimentId,
        'only-active',
        onlyActive,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentKlaviyoTemplates: makeQuery(
    getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
    (organization: string, spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: [
        'organization',
        organization,
        'sp-optimizer-flow-message-experiment-klaviyo-templates',
        spOptimizerFlowMessageExperimentId,
      ],
    }),
  ),
  organizationMembers: makeQuery(getOrganizationMembers, (slug: string) => ({
    queryKey: ['organization', slug, 'members'],
  })),
  pretesterInboxes: makeQuery(getPretesterInboxes, () => ({
    queryKey: ['pretester-inboxes'],
  })),
  slackChannelMappings: makeQuery(getSlackChannelMappings, () => ({
    queryKey: ['slack-channel-mappings'],
  })),
  slackAnnouncements: makeQuery(getSlackAnnouncements, (page = 1) => ({
    queryKey: ['slack-announcements', page],
    // Poll only while at least one announcement is still in flight
    // (PENDING/PROCESSING). Once everything resolves the page goes quiet, so
    // history-only viewers don't generate continuous load.
    refetchInterval: (query: { state: { data?: Awaited<ReturnType<typeof getSlackAnnouncements>> } }) => {
      const rows = query.state.data?.results ?? [];
      const inFlight = rows.some((r) => r.status === 'PENDING' || r.status === 'PROCESSING');
      return inFlight ? 30_000 : false;
    },
  })),
  slackAnnouncement: makeQuery(getSlackAnnouncement, (id: number) => ({
    queryKey: ['slack-announcement', id],
    // Retrieve has its own poll because the app's QueryClient sets
    // staleTime: Infinity and disables refetch-on-mount; without this an
    // expanded row's per-channel deliveries would never refresh.
    refetchInterval: (query: { state: { data?: Awaited<ReturnType<typeof getSlackAnnouncement>> } }) => {
      const deliveries = query.state.data?.deliveries ?? [];
      const inFlight = deliveries.some((d) => d.status === 'PENDING' || d.status === 'CLAIMED');
      return inFlight ? 30_000 : false;
    },
  })),
  browserAutomationHealth: makeQuery(getBrowserAutomationHealth, () => ({
    queryKey: ['browser-automation-health'],
    // Fixed poll: the snapshot aggregates queue + run state that changes regardless of what this
    // page shows, and the global QueryClient sets staleTime: Infinity / no refetch-on-mount.
    refetchInterval: 30_000,
  })),
  optInMonitoringStores: makeQuery(
    getOptInMonitoringStores,
    (store: string | undefined, days: number, startDate?: string, endDate?: string) => ({
      queryKey: ['opt-in-monitoring', 'stores', store, days, startDate, endDate],
    }),
  ),
  optInMonitoringStoreSummary: makeQuery(
    getOptInMonitoringStoreSummary,
    (store: string | undefined, days: number, startDate?: string, endDate?: string) => ({
      queryKey: ['opt-in-monitoring', 'store-summary', store, days, startDate, endDate],
    }),
  ),
  optInMonitoringBaseline: makeQuery(
    getOptInMonitoringBaseline,
    (store: string | undefined, days: number, startDate?: string, endDate?: string) => ({
      queryKey: ['opt-in-monitoring', 'baseline', store, days, startDate, endDate],
    }),
  ),
  optInMonitoringReadback: makeQuery(
    getOptInMonitoringReadback,
    (store: string | undefined, days: number, startDate?: string, endDate?: string) => ({
      queryKey: ['opt-in-monitoring', 'readback', store, days, startDate, endDate],
    }),
  ),
  optInMonitoringCompliance: makeQuery(
    getOptInMonitoringCompliance,
    (store: string | undefined, days: number, startDate?: string, endDate?: string) => ({
      queryKey: ['opt-in-monitoring', 'compliance', store, days, startDate, endDate],
    }),
  ),
  optInMonitoringAuditLog: makeQuery(getOptInMonitoringAuditLog, (query) => ({
    queryKey: ['opt-in-monitoring', 'audit-log', query],
  })),
  // Artifacts and console log are fetched only when an evidence drawer opens (the drawer renders
  // them only while it has a target), so everything below is paid for only while someone is
  // actually looking at a run.
  //
  // The content is immutable once a run has finished, but the signed URLs in the manifest are not:
  // they last an hour. So staleTime is a freshness window rather than a license to cache forever,
  // `refetchOnMount` re-mints on reopen past it, and `refetchInterval` re-mints under a drawer
  // left open longer than that. All three overrides are load-bearing — the global QueryClient sets
  // `staleTime: Infinity` and `refetchOnMount: false`, which on their own would serve expired URLs
  // for the life of the tab.
  //
  // `placeholderData: () => undefined` overrides the global `keepPreviousData`: the key carries
  // the session id, so clicking Evidence on a second row would otherwise render the first row's
  // screenshots and log under the second row's header until the fetch landed. It has to be a
  // function — `exactOptionalPropertyTypes` rejects a literal `undefined` here.
  //
  // Written out rather than built with makeQuery because the key carries something that is not an
  // argument to the endpoint: the manifest is not immutable at finished_at — recording capture
  // runs detached and fills in gcs_recording_path afterwards — so a drawer opened before that copy
  // landed must not keep serving its recording-less manifest once the row says there is one.
  baSessionArtifacts: (id: number, hasRecording: boolean) => ({
    queryFn: () => getBASessionArtifacts(id),
    queryKey: ['ba-session-artifacts', id, hasRecording],
    staleTime: 30 * 60_000,
    refetchOnMount: true,
    refetchInterval: 30 * 60_000,
    placeholderData: () => undefined,
  }),
  baSessionConsoleLog: makeQuery(getBASessionConsoleLog, (id: number) => ({
    queryKey: ['ba-session-console-log', id],
    staleTime: 30 * 60_000,
    refetchOnMount: true,
    placeholderData: () => undefined,
  })),
  browserAutomationSessions: makeQuery(getBrowserAutomationSessions, (page = 1, pageSize = 50, search = '') => ({
    // Key on the trimmed term because getBrowserAutomationSessions trims before sending: an untrimmed
    // caller would otherwise get its own cache entry — and its own poller — for a request identical on
    // the wire to the clean one.
    queryKey: ['browser-automation-sessions', page, pageSize, search.trim()],
    // Fast poll while a session is live (the iframe viewer wants fresh status); slow poll
    // otherwise so newly started sessions still appear. Global QueryClient sets staleTime:Infinity.
    refetchInterval: (query: { state: { data?: Awaited<ReturnType<typeof getBrowserAutomationSessions>> } }) => {
      const rows = query.state.data?.results ?? [];
      const running = rows.some((r) => r.status === 'RUNNING');
      return running ? 5_000 : 30_000;
    },
  })),
  // Backs the live-view panel. Fetched by id rather than read off the paginated list, so the panel
  // survives paging away from the watched run — and keeps polling its status, so it closes itself when
  // the run finishes wherever that row currently sits.
  browserAutomationSession: makeQuery(getBrowserAutomationSession, (id: number) => ({
    queryKey: ['browser-automation-session', id],
    // Stops dead once the run is terminal. Nothing reads the row after that — the panel is gated on
    // RUNNING — and the Close button lives *inside* the panel this status change just unmounted, so an
    // unconditional interval would have no reachable off switch and poll until the operator navigated
    // away from the page.
    refetchInterval: (query: { state: { data?: Awaited<ReturnType<typeof getBrowserAutomationSession>> } }) =>
      query.state.data?.status === 'RUNNING' ? 5_000 : false,
  })),
  csBrowserSessions: makeQuery(getCSBrowserSessions, (identity: 'team' | 'support', pageSize = 20) => ({
    queryKey: ['cs-browser-sessions', identity, pageSize],
    refetchInterval: 30_000,
  })),
  campaignCoverageAlerts: makeQuery(getCampaignCoverageAlerts, (weekStart?: string) => ({
    queryKey: ['campaign-coverage-alerts', weekStart ?? 'latest'],
  })),
  pendingFlowMessages: makeQuery(
    getAllPendingFlowMessages,
    (page = 1, pageSize = 200, tab: 'pending' | 'skipped' = 'pending', storeKeyName?: string) => ({
      queryKey: ['pending-flow-messages', page, pageSize, tab, storeKeyName],
    }),
  ),
  orderRateLiftAlerts: makeQuery(getOrderRateLiftAlerts, (weekStart?: string, showSuppressed?: boolean) => ({
    queryKey: ['order-rate-lift-alerts', weekStart ?? 'latest', showSuppressed ?? false],
  })),
  setupDecisions: makeQuery(getSetupDecisions, (filters) => ({
    queryKey: ['setup-decisions', filters?.status ?? 'all', filters?.spOptimizerStore ?? 'all'],
  })),
  misconfiguredExperiments: makeQuery(getAllMisconfiguredExperiments, () => ({
    queryKey: ['misconfigured-experiments'],
  })),
  dataQualityReport: makeQuery(getDataQualityReport, () => ({
    queryKey: ['data-quality-report'],
  })),
  needUniversalBlockExperiments: makeQuery(
    getAllNeedUniversalBlockExperiments,
    (page = 1, storeKeyName?: string, flowMessageId?: string) => ({
      queryKey: ['need-universal-block-experiments', page, storeKeyName, flowMessageId],
    }),
  ),
  flowMessagesNeedDeliverabilityBlockUpgrade: makeQuery(
    getAllFlowMessagesNeedDeliverabilityBlockUpgrade,
    (page = 1, storeKeyName?: string, flowMessageId?: string) => ({
      queryKey: ['flow-messages-need-deliverability-block-upgrade', page, storeKeyName, flowMessageId],
    }),
  ),
  campaignPrimaryConversionMetricOptions: makeQuery(
    getCampaignPrimaryConversionMetricOptions,
    (organization: string, spOptimizerStoreId: number) => ({
      queryKey: ['organization', organization, 'spOptimizerStore', spOptimizerStoreId, 'campaign-pcm-options'],
    }),
  ),
  optimizerCampaignDeliverabilityExperimentMetrics: makeQuery(
    getOptimizerCampaignDeliverabilityExperimentMetrics,
    (
      organization: string,
      spOptimizerStoreID: number,
      days: number,
      startDate?: string,
      endDate?: string,
      includeLikelihood = false,
    ) => ({
      queryKey: [
        'organization',
        organization,
        'optimizer-campaign-deliverability-experiment-results',
        spOptimizerStoreID,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
        'include-likelihood',
        includeLikelihood,
      ],
    }),
  ),
  // oxlint-disable-next-line typescript/no-explicit-any
} as const satisfies Record<string, (...args: any[]) => WithRequired<QueryOptions<any>, 'queryKey' | 'queryFn'>>;
